import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from "@ngrx/operators";
import { Store } from "@ngrx/store";
import { catchError, map, filter, switchMap, concatMap, distinctUntilChanged } from "rxjs/operators";
import { of, merge } from "rxjs";
import { MeasureFavoritesService } from "@services/measure-favorites.service";
import { AddOrganizationActions } from "@store/actions/add-organization/add-organization.actions";
import { DashboardActions } from "@store/actions/dashboard/dashboard.actions";
import { UserActions } from "@store/actions/user/user.actions";
import { DashboardService } from "@services/dashboard.service";
import {
  selectDecilesYear,
  selectIsSmallPractice,
  selectProgramYear,
  selectSavedFilters,
  selectSelectedMeasureCode,
  selectSelectedMonthlyTrendYears,
} from "@store/selectors/dashboard/dashboard.selectors";
import { selectUserOrgId } from "@store/selectors/user/user.selectors";

@Injectable()
export class DashboardEffects {
  constructor(
    private actions$: Actions,
    private dashboardService: DashboardService,
    private store: Store,
    private favoritesService: MeasureFavoritesService,
  ) {}

  loadDashboardMipsScoresOnInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.setUserOrganizationId),
      concatLatestFrom(() => this.store.select(selectProgramYear)),
      map(([action, programYear]) =>
        DashboardActions.loadDashboardMipsScores({
          orgId: +action.id,
          reportingYear: programYear,
          request: { clinician: ['all'], location: ['all'], measureYear: programYear },
        })
      ),
    )
  );

  loadDashboardMipsScoresOnFiltersChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.applyFilters),
      concatLatestFrom(() => this.store.select(selectIsSmallPractice)),
      map(([action, isSmallPractice]) => {
        const specific = action.filters.clinician.filter((c) => c !== 'all');
        const clinician = specific.length > 0 ? specific : ['all'];
        return DashboardActions.loadDashboardMipsScores({
          orgId: action.orgId,
          reportingYear: action.filters.measureYear,
          request: {
            clinician,
            location: action.filters.location,
            measureYear: +action.filters.measureYear,
            isSmallPracticeOverride: isSmallPractice || null,
            useTinAggregation: action.filters.useTinAggregation ?? false,
          },
        });
      }),
    )
  );

  loadDashboardMipsScoresOnSmallPracticeChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.setSmallPractice),
      concatLatestFrom(() => [
        this.store.select(selectUserOrgId),
        this.store.select(selectProgramYear),
        this.store.select(selectSavedFilters),
      ]),
      map(([action, orgId, programYear, savedFilters]) =>
        DashboardActions.loadDashboardMipsScores({
          orgId,
          reportingYear: programYear,
          request: {
            clinician: savedFilters?.clinician ?? ['all'],
            location: savedFilters?.location ?? ['all'],
            measureYear: programYear,
            isSmallPracticeOverride: action.isSmallPractice,
            useTinAggregation: savedFilters?.isTinAggregationEnabled ?? false,
          },
        })
      ),
    )
  );

  loadDashboardMipsScores$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadDashboardMipsScores),
      switchMap(({ orgId, request }) =>
        this.dashboardService.getDashboardMipsScores(orgId, request).pipe(
          map((data) => DashboardActions.loadDashboardMipsScoresSuccess({ data })),
          catchError((error) =>
            of(DashboardActions.loadDashboardMipsScoresFailure({
              error: error?.message ?? 'Failed to load MIPS scores',
            }))
          ),
        )
      ),
    )
  );

  loadIsMultiOrgTin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddOrganizationActions.getOrganizationDetailsSuccess),
      map((action) =>
        DashboardActions.setIsMultiOrgTin({
          isMultiOrgTin: action.details.isMultiOrgTin === true,
        })
      ),
    )
  );

  getLocations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.setUserOrganizationId),
      concatLatestFrom(() => this.store.select(selectProgramYear)),
      switchMap(([action, programYear]) => {
        return this.dashboardService.getDashboardLocations(+action.id, programYear).pipe(
          map((locations) => {
            return DashboardActions.getDashboardLocationsSuccess({ locations });
          }),
          catchError((error) => {
            return of(
              DashboardActions.getDashboardLocationsFailure({
                error: error || "Failed to load locations",
              }),
            );
          }),
        );
      }),
    );
  });

  getLocationsOnYearChange$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.applyFilters),
      switchMap((action) => {
        return this.dashboardService.getDashboardLocations(action.orgId, action.filters.measureYear).pipe(
          map((locations) => {
            return DashboardActions.getDashboardLocationsSuccess({ locations });
          }),
          catchError((error) => {
            return of(
              DashboardActions.getDashboardLocationsFailure({
                error: error || "Failed to load locations",
              }),
            );
          }),
        );
      }),
    );
  });

  getProviders$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.setUserOrganizationId),
      switchMap((action) => {
        return this.dashboardService.getDashboardProviders(+action.id).pipe(
          map((providers) => {
            return DashboardActions.getDashboardProvidersSuccess({ providers });
          }),
          catchError((error) => {
            return of(
              DashboardActions.getDashboardProvidersFailure({
                error: error || "Failed to load providers",
              }),
            );
          }),
        );
      }),
    );
  });

  getMeasureMonthlyTrend$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.selectDetailsTab),
      filter(({ tab }) => tab == "monthlyTrend"),
      concatLatestFrom(() => [
        this.store.select(selectUserOrgId),
        this.store.select(selectSelectedMeasureCode),
        this.store.select(selectSelectedMonthlyTrendYears),
      ]),
      switchMap(([_action, orgId, code, years]) => {
        if (!years || years.length === 0) {
          return of(DashboardActions.getMeasureMonthlyTrendSuccess({ trend: [] }));
        }
        if (typeof code != "string") {
          code = "";
        }
        return this.dashboardService
          .getMeasureMonthlyTrend(orgId, code, years)
          .pipe(
            map((trend) => {
              return DashboardActions.getMeasureMonthlyTrendSuccess({ trend });
            }),
            catchError((error) =>
              of(DashboardActions.getMeasureMonthlyTrendFailure({ error })),
            ),
          );
      }),
    );
  });

  getMeasureMonthlyTrendOnYearChange$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.monthlyTrendMonthsChanged),
      concatLatestFrom(() => [
        this.store.select(selectUserOrgId),
        this.store.select(selectSelectedMeasureCode),
        this.store.select(selectSelectedMonthlyTrendYears),
      ]),
      switchMap(([_action, orgId, code, years]) => {
        if (!years || years.length === 0) {
          return of(DashboardActions.getMeasureMonthlyTrendSuccess({ trend: [] }));
        }
        if (typeof code != "string") {
          code = "";
        }
        return this.dashboardService
          .getMeasureMonthlyTrend(orgId, code, years)
          .pipe(
            map((trend) => {
              return DashboardActions.getMeasureMonthlyTrendSuccess({ trend });
            }),
            catchError((error) =>
              of(DashboardActions.getMeasureMonthlyTrendFailure({ error })),
            ),
          );
      }),
    );
  });


  loadDashboardDecilesOnYearChange$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(UserActions.setUserOrganizationId),
        concatLatestFrom(() => this.store.select(selectProgramYear)),
        map(([, year]) => year),
      ),
      this.actions$.pipe(
        ofType(DashboardActions.applyFilters),
        map((action) => +action.filters.measureYear),
      ),
    ).pipe(
      distinctUntilChanged(),
      concatLatestFrom(() => this.store.select(selectDecilesYear)),
      filter(([year, cachedYear]) => year !== cachedYear),
      map(([year]) => DashboardActions.loadDashboardDeciles({ year })),
    )
  );

  loadDashboardDeciles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadDashboardDeciles),
      switchMap(({ year }) =>
        this.dashboardService.getDashboardDeciles(year).pipe(
          map((deciles) => DashboardActions.loadDashboardDecilesSuccess({ deciles, year })),
          catchError((error) =>
            of(DashboardActions.loadDashboardDecilesFailure({
              error: error?.message ?? 'Failed to load deciles',
            }))
          ),
        )
      ),
    )
  );

  calculateMipsReweighting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.calculateMipsReweighting),
      switchMap(({ request }) =>
        this.dashboardService.calculateMipsReweighting(request).pipe(
          map((data) => DashboardActions.calculateMipsReweightingSuccess({ data })),
          catchError((error) =>
            of(DashboardActions.calculateMipsReweightingFailure({
              error: error?.message ?? 'Failed to calculate MIPS reweighting',
            }))
          ),
        )
      ),
    )
  );

  loadMeasureFavoritesOnLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getUserOnLoginSuccess),
      switchMap(({ user }) =>
        this.favoritesService.getFavorites(user.id).pipe(
          map((measureCodes) => DashboardActions.loadMeasureFavoritesSuccess({ measureCodes })),
          catchError((error) =>
            of(DashboardActions.loadMeasureFavoritesFailure({ error: error?.message ?? 'Failed to load favorites' }))
          ),
        )
      ),
    )
  );

  toggleMeasureFavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.toggleMeasureFavorite),
      concatMap(({ userId, measureCode, isFavorite }) => {
        const call$ = isFavorite
          ? this.favoritesService.removeFavorite(userId, measureCode)
          : this.favoritesService.addFavorite(userId, measureCode);
        return call$.pipe(
          map(() =>
            isFavorite
              ? DashboardActions.removeMeasureFavoriteSuccess({ measureCode })
              : DashboardActions.addMeasureFavoriteSuccess({ measureCode })
          ),
          catchError((error) =>
            of(
              isFavorite
                ? DashboardActions.removeMeasureFavoriteFailure({ error: error?.message ?? 'Failed to remove favorite' })
                : DashboardActions.addMeasureFavoriteFailure({ error: error?.message ?? 'Failed to add favorite' })
            )
          ),
        );
      }),
    )
  );
}
